import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { getFeatures } from "../../api/insightsClient";

const navigation = [
    { name: "Dashboard", href: "/portfolio", icon: LayoutDashboard },
    { name: "Accounts", href: "/accounts", icon: Wallet },
    { name: "Insights", href: "/insights", icon: Lightbulb, featureFlag: "insights" },
    { name: "Goals", href: "/goals", icon: Target },
    { name: "Investments", href: "/investments", icon: TrendingUp },
    { name: "Projections", href: "/projections", icon: Calculator },
];

const secondaryNav = [
    { name: "Profile", href: "/profile", icon: User },
    { name: "Settings", href: "/settings", icon: Settings },
];

// Simple icon components
function LayoutDashboard({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
    );
}

function Wallet({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
            <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
            <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
        </svg>
    );
}

function TrendingUp({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
            <polyline points="16 7 22 7 22 13" />
        </svg>
    );
}

function Target({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="6" />
            <circle cx="12" cy="12" r="2" />
        </svg>
    );
}

function Calculator({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <rect x="4" y="2" width="16" height="20" rx="2" />
            <line x1="8" y1="6" x2="16" y2="6" />
            <line x1="8" y1="10" x2="8" y2="10.01" />
            <line x1="12" y1="10" x2="12" y2="10.01" />
            <line x1="16" y1="10" x2="16" y2="10.01" />
            <line x1="8" y1="14" x2="8" y2="14.01" />
            <line x1="12" y1="14" x2="12" y2="14.01" />
            <line x1="16" y1="14" x2="16" y2="14.01" />
            <line x1="8" y1="18" x2="8" y2="18.01" />
            <line x1="12" y1="18" x2="16" y2="18" />
        </svg>
    );
}

function User({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <circle cx="12" cy="8" r="4" />
            <path d="M20 21a8 8 0 1 0-16 0" />
        </svg>
    );
}

function Settings({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <circle cx="12" cy="12" r="3" />
            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
        </svg>
    );
}

function Menu({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="4" y1="18" x2="20" y2="18" />
        </svg>
    );
}

function X({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

function Shield({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
    );
}

function LogOut({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
    );
}

function Lock({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
    );
}

function Lightbulb({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" />
        </svg>
    );
}

function SidebarLink({ item, onClick, locked }) {
    if (locked) {
        return (
            <NavLink
                to="/subscribe"
                onClick={onClick}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 cursor-default"
            >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.name}</span>
                <Lock className="w-3.5 h-3.5" />
            </NavLink>
        );
    }

    return (
        <NavLink
            to={item.href}
            onClick={onClick}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    isActive
                        ? "bg-accent-50 text-accent-600"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`
            }
        >
            <item.icon className="w-5 h-5" />
            <span>{item.name}</span>
        </NavLink>
    );
}

const RESTRICTED_PATHS = new Set(["/portfolio", "/goals", "/investments", "/projections", "/insights"]);

function Sidebar({ mobileOpen, setMobileOpen }) {
    const { user, logout } = useAuth();
    const { hasActiveSubscription } = useSubscription();
    const navigate = useNavigate();
    const [features, setFeatures] = useState({});

    useEffect(() => {
        getFeatures().then(setFeatures).catch(() => {});
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const content = (
        <div className="flex flex-col h-full">
            {/* Logo */}
            <NavLink
                to="/"
                className="flex items-center gap-2.5 px-4 h-16 border-b border-gray-200 hover:bg-gray-50 transition-colors"
            >
                <div className="w-7 h-7 rounded-md bg-gray-900 flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">O</span>
                </div>
                <span className="font-semibold text-gray-900 tracking-tight">OmniWorth</span>
            </NavLink>

            {/* Navigation */}
            <nav className="flex-1 p-3 space-y-0.5">
                {!hasActiveSubscription && (
                    <NavLink
                        to="/subscribe"
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 mb-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                                isActive
                                    ? "bg-gray-900 text-white"
                                    : "bg-accent-50 text-accent-700 hover:bg-accent-100"
                            }`
                        }
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span>Start free trial</span>
                    </NavLink>
                )}
                {navigation
                    .filter((item) => !item.featureFlag || features[item.featureFlag])
                    .map((item) => (
                    <SidebarLink
                        key={item.name}
                        item={item}
                        onClick={() => setMobileOpen(false)}
                        locked={!hasActiveSubscription && RESTRICTED_PATHS.has(item.href)}
                    />
                ))}

                <div className="pt-4 mt-4 border-t border-gray-200 space-y-0.5">
                    {secondaryNav.map((item) => (
                        <SidebarLink key={item.name} item={item} onClick={() => setMobileOpen(false)} />
                    ))}
                    {user?.roles?.includes("ROLE_ADMIN") && (
                        <SidebarLink
                            item={{ name: "Admin", href: "/admin", icon: Shield }}
                            onClick={() => setMobileOpen(false)}
                        />
                    )}
                </div>
            </nav>

            {/* User & Logout */}
            <div className="p-3 border-t border-gray-200">
                <div className="flex items-center gap-3 px-2 mb-2">
                    <div className="w-8 h-8 bg-accent-100 rounded-full flex items-center justify-center">
                        <span className="text-accent-600 text-sm font-semibold">
                            {user?.email?.charAt(0).toUpperCase() || "U"}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                            {user?.displayName || user?.email?.split("@")[0]}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                </div>
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                    <LogOut className="w-5 h-5" />
                    <span>Log out</span>
                </button>
                <NavLink
                    to="/"
                    className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    <span>Back to home</span>
                </NavLink>
            </div>
        </div>
    );

    return (
        <>
            {/* Mobile overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-gray-900/40 z-40 lg:hidden"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Mobile sidebar */}
            <div
                className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform lg:hidden ${
                    mobileOpen ? "translate-x-0" : "-translate-x-full"
                }`}
            >
                <button
                    onClick={() => setMobileOpen(false)}
                    className="absolute top-4 right-4 p-1 text-gray-500 hover:text-gray-900"
                    aria-label="Close menu"
                >
                    <X className="w-5 h-5" />
                </button>
                {content}
            </div>

            {/* Desktop sidebar */}
            <div className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 bg-white border-r border-gray-200">
                {content}
            </div>
        </>
    );
}

function Header({ onMenuClick }) {
    return (
        <header className="sticky top-0 z-30 h-16 bg-white/90 backdrop-blur-sm border-b border-gray-200">
            <div className="flex items-center justify-between h-full px-4 lg:px-6">
                <button
                    onClick={onMenuClick}
                    className="lg:hidden p-2 text-gray-600 hover:text-gray-900"
                    aria-label="Open menu"
                >
                    <Menu className="w-5 h-5" />
                </button>

                <div className="flex-1" />

                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                        {new Date().toLocaleDateString("en-US", {
                            weekday: "long",
                            month: "short",
                            day: "numeric",
                        })}
                    </span>
                </div>
            </div>
        </header>
    );
}

function EarlyAccessBanner() {
    const [dismissed, setDismissed] = useState(() => {
        const dismissedAt = localStorage.getItem("earlyAccessBannerDismissedAt");
        if (!dismissedAt) return false;
        const daysSince = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
        return daysSince < 7;
    });

    if (dismissed) return null;

    return (
        <div className="bg-accent-50 border-b border-accent-100 px-4 py-2 flex items-center justify-between gap-4">
            <p className="text-xs text-gray-700 flex-1 text-center">
                OmniWorth is in early access. Found a bug or have feedback? Let us know at{" "}
                <a href="mailto:support@omniworth.net" className="text-accent-600 hover:underline font-medium">support@omniworth.net</a>
            </p>
            <button
                onClick={() => {
                    setDismissed(true);
                    localStorage.setItem("earlyAccessBannerDismissedAt", Date.now().toString());
                }}
                className="text-gray-500 hover:text-gray-900 shrink-0"
                aria-label="Dismiss banner"
            >
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}

export default function AppLayout({ children }) {
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <div className="min-h-screen bg-gray-50">
            <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

            <div className="lg:pl-64">
                <EarlyAccessBanner />
                <Header onMenuClick={() => setMobileOpen(true)} />
                <main className="p-4 lg:p-8" style={{ willChange: "transform" }}>
                    {children}
                </main>
            </div>
        </div>
    );
}
